/* eslint-disable no-unused-vars */
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable react/jsx-curly-newline */
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import closeSvg from 'src/assets/close.svg';
import {
  CommonDropdown,
  CommonInput,
  CustomCheckBox,
  DatePicker,
  FormErrorWrap,
  FullScreenLoading,
  ICommonDropdownOption,
  RegularButton,
} from 'pg-ui-library';
import ReactModal from 'react-modal';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { Gender, IDependent, MaritalStatus, RelationshipType } from 'src/interfaces';
import { phoneRegExp } from 'src/utils/phoneRegex';
import { CloseIcon, DependentsForm, FormInputWrapper, ActionWrapper } from './styles';
import { useAddDependentToEmployee } from '../../mutations/employee-add-dependent.mutation';

export const maritalStatusOptions: ICommonDropdownOption[] = [
  {
    key: '1',
    label: MaritalStatus.SINGLE,
    value: MaritalStatus.SINGLE,
  },
  {
    key: '2',
    label: MaritalStatus.MARRIED,
    value: MaritalStatus.MARRIED,
  },
  {
    key: '3',
    label: MaritalStatus.DIVORCED,
    value: MaritalStatus.DIVORCED,
  },
  {
    key: '4',
    label: MaritalStatus.NOT_APPLICABLE,
    value: MaritalStatus.NOT_APPLICABLE,
  },
];

export const genderOptions: ICommonDropdownOption[] = [
  {
    key: '1',
    label: Gender.MALE,
    value: Gender.MALE,
  },
  {
    key: '2',
    label: Gender.FEMALE,
    value: Gender.FEMALE,
  },
  {
    key: '3',
    label: Gender.NOT_APPLICABLE,
    value: Gender.NOT_APPLICABLE,
  },
];

export const relationshipOptions: ICommonDropdownOption[] = [
  {
    key: '1',
    label: RelationshipType.HUSBAND,
    value: RelationshipType.HUSBAND,
  },
  {
    key: '2',
    label: RelationshipType.SON,
    value: RelationshipType.SON,
  },
  {
    key: '3',
    label: RelationshipType.SPOUSE,
    value: RelationshipType.SPOUSE,
  },
  {
    key: '4',
    label: RelationshipType.DAUGHTER,
    value: RelationshipType.DAUGHTER,
  },
  {
    key: '5',
    label: RelationshipType.Family,
    value: RelationshipType.Family,
  },
  {
    key: '6',
    label: RelationshipType.Parent,
    value: RelationshipType.Parent,
  },
  {
    key: '7',
    label: RelationshipType.Sibling,
    value: RelationshipType.Sibling,
  },
  {
    key: '8',
    label: RelationshipType.CHILD,
    value: RelationshipType.CHILD,
  },
  {
    key: '9',
    label: RelationshipType.DOMESTIC_PARTNER_LABEL,
    value: RelationshipType.DOMESTIC_PARTNER,
  },
  {
    key: '10',
    label: RelationshipType.EX_SPOUSE_LABEL,
    value: RelationshipType.EX_SPOUSE,
  },
  {
    key: '11',
    label: RelationshipType.FAMILY_CONTACT_LABEL,
    value: RelationshipType.FAMILY_CONTACT,
  },
  {
    key: '12',
    label: RelationshipType.OTHER_CONTACT_LABEL,
    value: RelationshipType.OTHER_CONTACT,
  },
];

export enum FormPages {
  // eslint-disable-next-line no-unused-vars
  PERSONAL = 'Personal',
  ADDRESS = 'Address',
}

const PersonalDependentsForm: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState(FormPages.PERSONAL);
  const addDependentToEmploye = useAddDependentToEmployee();
  const { selectedEmployee } = useTypedSelector(state => state.employees.data);
  const { config } = useTypedSelector(state => state.config.data);

  const personnelNumber = selectedEmployee.employee?.employeeId || '';
  const companyId = config?.selectedCompanyId || '';

  const validationSchema = yup.object({
    firstName: yup.string().max(255).required(),
    middleName: yup.string().max(255),
    lastName: yup.string().max(255).required(),
    workPhone: yup.string().max(14).matches(phoneRegExp, t('global.form.invalidPhone')),
    homePhone: yup.string().max(14).matches(phoneRegExp, t('global.form.invalidPhone')),
    email: yup.string().email(),
    birthDate: yup.string().nullable().required(),
    ssn: yup
      .string()
      .length(9, 'SSN must be exactly nine-digit number')
      .matches(/^\d+$/, 'SSN is required only numeric digits'),
    zipCode: yup
      .string()
      .length(5, 'Zip Code must be exactly five-digit number')
      .matches(/^\d+$/, 'Zip Code is required only numeric digits'),
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      middleName: '',
      lastName: '',
      ssn: '',
      workPhone: '',
      homePhone: '',
      email: '',
      city: '',
      address: '',
      state: '',
      zipCode: '',
      country: 'USA',
      county: '',
      notes: '',
      birthDate: new Date(),
      relationshipType: RelationshipType.HUSBAND,
      gender: Gender.MALE,
      isPersonalContact: false,
      isEmergencyContact: false,
      isBeneficiary: false,
      isDependent: true,
      studentStatus: false,
      smokerStatus: false,
    },
    validationSchema,
    onSubmit: async (values: IDependent) => {
      await addDependentToEmploye.mutateAsync({
        dependent: values,
        params: { personnelNumber, companyId },
      });
      onSuccess();
    },
  });

  return (
    <>
      {addDependentToEmploye.isLoading && <FullScreenLoading />}
      <DependentsForm onSubmit={formik.handleSubmit}>
        {formState === FormPages.PERSONAL ? (
          <>
            <FormInputWrapper>
              <CommonInput
                id="firstName"
                name="firstName"
                label="First Name*"
                placeholder="First Name"
                value={formik.values.firstName}
                onChange={event => {
                  const input = event.target.value;
                  if (/^[A-Za-z]*$/.test(input) || input === '') {
                    formik.setFieldValue('firstName', input);
                  }
                }}
                style={{ width: '100%' }}
              />
              <FormErrorWrap errorMessage={formik.errors.firstName as string} visible={!!formik.errors.firstName} />
            </FormInputWrapper>

            <FormInputWrapper>
              <CommonInput
                id="middleName"
                name="middleName"
                label="Middle Name"
                placeholder="Middle Name"
                value={formik.values.middleName}
                onChange={event => {
                  const input = event.target.value;
                  if (/^[A-Za-z]*$/.test(input) || input === '') {
                    formik.setFieldValue('middleName', input);
                  }
                }}
                style={{ width: '100%' }}
              />
              <FormErrorWrap errorMessage={formik.errors.middleName as string} visible={!!formik.errors.middleName} />
            </FormInputWrapper>

            <FormInputWrapper>
              <CommonInput
                id="lastName"
                name="lastName"
                label="Last Name*"
                placeholder="Last Name"
                value={formik.values.lastName}
                onChange={event => {
                  const input = event.target.value;
                  if (/^[A-Za-z]*$/.test(input) || input === '') {
                    formik.setFieldValue('lastName', input);
                  }
                }}
                style={{ width: '100%' }}
              />
              <FormErrorWrap errorMessage={formik.errors.lastName as string} visible={!!formik.errors.lastName} />
            </FormInputWrapper>

            <FormInputWrapper>
              <CommonDropdown
                label="Relationship"
                id="relationshipType"
                name="relationshipType"
                options={relationshipOptions}
                value={formik.values.relationshipType}
                setValue={(value: string) => formik.setFieldValue('relationshipType', value)}
                selectStyle={{
                  width: '100%',
                  maxWidth: '100%',
                  marginTop: '8px',
                }}
              />
            </FormInputWrapper>

            <FormInputWrapper>
              <CommonDropdown
                label="Gender"
                id="gender"
                name="gender"
                options={genderOptions}
                value={formik.values.gender}
                setValue={(value: string) => formik.setFieldValue('gender', value)}
                selectStyle={{
                  width: '100%',
                  maxWidth: '100%',
                  marginTop: '8px',
                }}
              />
            </FormInputWrapper>

            <FormInputWrapper>
              <CommonInput
                id="ssn"
                name="ssn"
                label="SSN"
                placeholder="SSN"
                value={formik.values.ssn}
                onChange={event => {
                  const ssnValue = event.target.value.replace(/\D/g, '').substring(0, 9);
                  formik.setFieldValue('ssn', ssnValue);
                }}
                style={{ width: '100%' }}
              />
              <FormErrorWrap errorMessage={formik.errors.ssn as string} visible={!!formik.errors.ssn} />
            </FormInputWrapper>

            <FormInputWrapper>
              <DatePicker
                id="birthDate"
                name="birthDate"
                label="Birth Date*"
                dateFormat="MM/dd/yyyy"
                maxDate={new Date()}
                selected={formik.values.birthDate as Date}
                onChange={(val: Date) => {
                  formik.setFieldValue('birthDate', val);
                }}
              />
              <FormErrorWrap errorMessage={formik.errors.birthDate as string} visible={!!formik.errors.birthDate} />
            </FormInputWrapper>

            <FormInputWrapper>
              <CommonInput
                id="workPhone"
                name="workPhone"
                label="Work Phone"
                placeholder="Work Phone"
                value={formik.values.workPhone}
                onChange={event => {
                  const inputValue = event.target.value;
                  const formattedValue = inputValue
                    .replace(/[^\d!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/g, '')
                    .substring(0, 14);
                  formik.setFieldValue('workPhone', formattedValue);
                }}
                style={{ width: '100%' }}
              />
              <FormErrorWrap errorMessage={formik.errors.workPhone as string} visible={!!formik.errors.workPhone} />
            </FormInputWrapper>

            <FormInputWrapper>
              <CommonInput
                id="homePhone"
                name="homePhone"
                label="Home Phone"
                placeholder="Home Phone"
                value={formik.values.homePhone}
                onChange={event => {
                  const inputValue = event.target.value;
                  const formattedValue = inputValue
                    .replace(/[^\d!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/g, '')
                    .substring(0, 14);
                  formik.setFieldValue('workPhone', formattedValue);
                }}
                style={{ width: '100%' }}
              />
              <FormErrorWrap errorMessage={formik.errors.homePhone as string} visible={!!formik.errors.homePhone} />
            </FormInputWrapper>

            <FormInputWrapper>
              <CommonInput
                id="email"
                name="email"
                label="Email"
                placeholder="Email"
                value={formik.values.email}
                onChange={event => formik.setFieldValue('email', event.target.value)}
                style={{ width: '100%' }}
              />
            </FormInputWrapper>

            <FormInputWrapper />
            <FormInputWrapper />

            <FormInputWrapper>
              <CustomCheckBox
                label="is an Emergency Contact?"
                id="isEmergencyContact"
                name="isEmergencyContact"
                checked={formik.values.isEmergencyContact}
                onChange={() => {
                  formik.setFieldValue('isEmergencyContact', !formik.values.isEmergencyContact);
                }}
              />
            </FormInputWrapper>

            <FormInputWrapper>
              <CustomCheckBox
                label="is a Personal Contact?"
                id="isPersonalContact"
                name="isPersonalContact"
                checked={formik.values.isPersonalContact}
                onChange={() => {
                  formik.setFieldValue('isPersonalContact', !formik.values.isPersonalContact);
                }}
              />
            </FormInputWrapper>

            <FormInputWrapper>
              <CustomCheckBox
                label="is a Dependent?"
                id="isDependent"
                name="isDependent"
                checked={formik.values.isDependent}
                onChange={() => {
                  formik.setFieldValue('isDependent', !formik.values.isDependent);
                }}
              />
            </FormInputWrapper>
          </>
        ) : (
          <>
            <FormInputWrapper>
              <CommonInput
                id="address"
                name="address"
                label="Address"
                placeholder="Address"
                value={formik.values.address}
                onChange={event => formik.setFieldValue('address', event.target.value)}
                style={{ width: '100%' }}
              />
              <FormErrorWrap errorMessage={formik.errors.address as string} visible={!!formik.errors.address} />
            </FormInputWrapper>

            <FormInputWrapper>
              <CommonInput
                id="city"
                name="city"
                label="City"
                placeholder="City"
                value={formik.values.city}
                onChange={event => formik.setFieldValue('city', event.target.value)}
                style={{ width: '100%' }}
              />
              <FormErrorWrap errorMessage={formik.errors.city as string} visible={!!formik.errors.city} />
            </FormInputWrapper>

            <FormInputWrapper>
              <CommonInput
                id="state"
                name="state"
                label="State"
                placeholder="State"
                value={formik.values.state}
                onChange={event => formik.setFieldValue('state', event.target.value)}
                style={{ width: '100%' }}
              />
              <FormErrorWrap errorMessage={formik.errors.state as string} visible={!!formik.errors.state} />
            </FormInputWrapper>

            <FormInputWrapper>
              <CommonInput
                id="zipCode"
                name="zipCode"
                label="Zip Code"
                placeholder="Zip Code"
                value={formik.values.zipCode}
                onChange={event => {
                  const zipCodeValue = event.target.value.replace(/\D/g, '').substring(0, 5);
                  formik.setFieldValue('zipCode', zipCodeValue);
                }}
                style={{ width: '100%' }}
              />
              <FormErrorWrap errorMessage={formik.errors.zipCode as string} visible={!!formik.errors.zipCode} />
            </FormInputWrapper>

            <FormInputWrapper>
              <CommonInput
                id="country"
                name="country"
                label="Country"
                placeholder="Country"
                value={formik.values.country}
                onChange={event => formik.setFieldValue('country', event.target.value)}
                style={{ width: '100%' }}
              />
              <FormErrorWrap errorMessage={formik.errors.country as string} visible={!!formik.errors.country} />
            </FormInputWrapper>

            <FormInputWrapper>
              <CommonInput
                id="county"
                name="county"
                label="County"
                placeholder="County"
                value={formik.values.county}
                onChange={event => formik.setFieldValue('county', event.target.value)}
                style={{ width: '100%' }}
              />
              <FormErrorWrap errorMessage={formik.errors.county as string} visible={!!formik.errors.county} />
            </FormInputWrapper>

            <FormInputWrapper>
              <CommonInput
                id="notes"
                name="notes"
                label="Notes"
                placeholder="Notes"
                value={formik.values.notes}
                onChange={event => formik.setFieldValue('notes', event.target.value)}
                style={{ width: '100%' }}
              />
              <FormErrorWrap errorMessage={formik.errors.notes as string} visible={!!formik.errors.notes} />
            </FormInputWrapper>

            <FormInputWrapper />
            <FormInputWrapper />

            <FormInputWrapper>
              <CustomCheckBox
                label="Student?"
                id="studentStatus"
                name="studentStatus"
                checked={formik.values.studentStatus}
                onChange={() => {
                  formik.setFieldValue('studentStatus', !formik.values.studentStatus);
                }}
              />
            </FormInputWrapper>

            <FormInputWrapper>
              <CustomCheckBox
                label="Smoker?"
                id="smokerStatus"
                name="smokerStatus"
                checked={formik.values.smokerStatus}
                onChange={() => {
                  formik.setFieldValue('smokerStatus', !formik.values.smokerStatus);
                }}
              />
            </FormInputWrapper>
            <FormInputWrapper />
          </>
        )}
      </DependentsForm>
      <ActionWrapper>
        {formState === FormPages.PERSONAL ? (
          <RegularButton type="button" onClick={() => setFormState(FormPages.ADDRESS)}>
            {t('global.button.next')}
          </RegularButton>
        ) : (
          <>
            <RegularButton type="button" onClick={() => setFormState(FormPages.PERSONAL)}>
              {t('global.button.back')}
            </RegularButton>
            <RegularButton
              type="submit"
              onClick={() => formik.handleSubmit()}
              disabled={!formik.isValid || addDependentToEmploye.isLoading}
            >
              {t('global.form.save')}
            </RegularButton>
          </>
        )}
      </ActionWrapper>
    </>
  );
};

export const PersonalDependentsModal: React.FC<{
  isOpen: boolean;
  setIsOpen: Function;
}> = ({ isOpen, setIsOpen }) => {
  const modalRef = useRef(null);

  const handleModalClose = () => {
    setIsOpen(false);
  };

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '10px',
          margin: 'auto',
          maxWidth: '1200px',
          minWidth: '1200px',
          height: '670px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        },
      }}
      onRequestClose={handleModalClose}
      // eslint-disable-next-line no-return-assign
      contentRef={(node: any) => (modalRef.current = node)}
    >
      <CloseIcon src={closeSvg} onClick={handleModalClose} />
      <PersonalDependentsForm onSuccess={handleModalClose} />
    </ReactModal>
  );
};
